import { Show, useRecordContext, useNavigate } from 'react-admin';
import { Box, Typography, Paper, Chip, IconButton } from '@mui/material';
import { LocalOffer, Close } from '@mui/icons-material';

const TIER_COLORS: Record<string, string> = { Preferred: 'warning', Organic: 'info' };
const STATUS_COLORS: Record<string, string> = { Active: 'success', Expired: 'default', Cancelled: 'error' };

/**
 * Training Provider Promotion Detail view
 *
 * Domain: Skills Development > Training Provider Promotion
 * Description: Detail view for a single promotion record.
 * @see Documentation/Specification/020_Definitions/application-specific/hr/skills-development/TrainingProviderPromotion.md
 */
const PromotionShowContent = () => {
  const record = useRecordContext();
  const navigate = useNavigate();

  if (!record) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/training-provider-promotions')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1565c0, #1976d2)', color: 'primary.contrastText', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalOffer sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Provider Promotion</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {record.trainingProvider?.id ?? '—'}
              </Typography>
            </Box>
          </Box>
          <Chip label={record.status} size="medium" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        {[
          { label: 'Tier', value: record.tier, chip: true },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Start Date', value: record.startDate ? new Date(record.startDate).toLocaleDateString() : '—' },
          { label: 'End Date', value: record.endDate ? new Date(record.endDate).toLocaleDateString() : '—' },
          { label: 'Provider ID', value: record.trainingProvider?.id ?? '—' },
          { label: 'OFO Codes', value: record.ofoCodes?.join(', ') || '—' },
        ].map(f => (
          <Paper key={f.label} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">{f.label}</Typography>
            {f.chip ? (
              <Chip
                label={f.value}
                size="small"
                sx={{ mt: 0.5, bgcolor: `${STATUS_COLORS[f.value as string] ?? TIER_COLORS[f.value as string] ?? 'grey'}.main`, color: 'white', fontWeight: 600 }}
              />
            ) : (
              <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{f.value}</Typography>
            )}
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export const PromotionShow = () => (
  <Show>
    <PromotionShowContent />
  </Show>
);
